import { Button } from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { showError, showToast } from "../../../constants/toasts";
import {
  getFromStorage,
  removeFromStorage,
  setToStorage,
  STORAGE_KEYS,
} from "../../../helpers";
import { OFFER_STATUS } from "../../../utils/enums";
import { Offer, OfferData } from "../../../types/General";
import {
  useCreateOfferMutation,
  useLazyCloneOfferQuery,
  useUpdateOfferMutation,
} from "../../../service/offer";

interface OthersFormProps {
  alignment: string;
  offerDetails: Offer | null;
  updateId: string | undefined;
  isClone?: boolean;
  tabs: number;
  setTabs: Dispatch<SetStateAction<number>>;
  categoryTab: any;
  status: any;
  categoryName: string;
  categoryId: string;
}

function ExperienceForm({
  alignment,
  offerDetails,
  updateId,
  isClone,
  tabs,
  setTabs,
  categoryId,
  categoryName,
  categoryTab,
  status,
}: OthersFormProps) {
  const navigate = useNavigate();
  const [createOffer] = useCreateOfferMutation();
  const [updateOffer] = useUpdateOfferMutation();
  const [cloneOffer] = useLazyCloneOfferQuery();

  const offerData = getFromStorage(STORAGE_KEYS.offerData);
  const parsedData: OfferData | null = offerData
    ? (JSON.parse(offerData) as OfferData)
    : null;
  const [date, setDate] = useState<Dayjs | null>(null);
  const [time, setTime] = useState<Dayjs | null>(null);
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+971");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleChangePhone = (phone: any, country: any) => {
    setPhone(phone?.replace(country.dialCode, ""));
    setCountryCode(country?.dialCode);
  };

  const handleSubmit = () => {
    setError(true);

    if (!date || !time) {
      return;
    }

    if (!updateId && dayjs(date).isBefore(dayjs(), "day")) {
      return;
    }

    if (!phone) {
      return;
    }

    const newBody = {
      WhatsAppNumber: phone,
      CountryDialCode: countryCode,
      QuestionJson: [],
      IsActive: alignment === OFFER_STATUS.ACTIVE ? true : false,
      IsArchive: alignment === OFFER_STATUS.ARCHIVE ? true : false,
      DealerTime: time ? dayjs(time).toISOString() : "",
      DealerDate: date ? dayjs(date).toISOString() : "",
    };

    const prevData = getFromStorage(STORAGE_KEYS.offerData);
    let parsedPrevData = {};

    if (prevData) {
      try {
        parsedPrevData = JSON.parse(prevData);
      } catch (err) {
        console.error("Error parsing stored offer data:", err);
      }
    }

    // Merge old and new data
    const mergedData = {
      ...parsedPrevData,
      ...newBody,
    };

    setToStorage(STORAGE_KEYS.offerData, JSON.stringify(mergedData));
    navigate("/manage-offer/preview-offer?mode=EXPERIENCE", {
      state: {
        updateId,
        isClone,
        status,
        categoryTab,
        categoryId,
        categoryName,
      },
    });
  };

  const handleArchive = async () => {
    const formData = new FormData();

    const appendIfPresent = (key: string, value: any) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    };

    const appendArray = (key: string, arr?: any[]) => {
      if (Array.isArray(arr)) {
        arr.forEach((item) => {
          if (item !== undefined && item !== null && item !== "") {
            formData.append(key, item);
          }
        });
      }
    };

    appendIfPresent("Title", parsedData?.Title);
    appendIfPresent("Badge", parsedData?.Badge);
    appendIfPresent("Description", parsedData?.Description);
    appendIfPresent("Subtitle", parsedData?.Subtitle);
    appendIfPresent("CategoryId", parsedData?.CategoryId);
    appendIfPresent("OfferType", parsedData?.OfferType);

    appendIfPresent(
      "OfferDisplayStartDate",
      parsedData?.OfferDisplayStartDate
        ? dayjs(parsedData?.OfferDisplayTime).toISOString()
        : null
    );

    appendIfPresent(
      "OfferDisplayEndDate",
      parsedData?.OfferDisplayEndDate
        ? dayjs(parsedData?.OfferDisplayEndTime).toISOString()
        : null
    );

    appendIfPresent(
      "IsButterflyEffect",
      parsedData?.IsButterflyEffect ? "true" : "false"
    );

    appendIfPresent(
      "ButterflyEffectStartDate",
      parsedData?.ButterflyEffectStartDate
        ? dayjs(parsedData?.ButterflyEffectStartTime).toISOString()
        : null
    );

    appendIfPresent(
      "ButterflyEffectEndDate", // fixed the duplicate key name
      parsedData?.ButterflyEffecteEndDate
        ? dayjs(parsedData?.ButterflyEffectEndTime).toISOString()
        : null
    );

    appendIfPresent("ButtonName", parsedData?.ButtonName);
    appendArray("GroupsId", parsedData?.GroupsId);
    appendArray("TagsId", parsedData?.TagsId);
    appendArray("offerImages", parsedData?.offerImages);

    appendIfPresent(
      "OfferDisplayTime",
      parsedData?.OfferDisplayTime
        ? dayjs(parsedData?.OfferDisplayTime).format("HH:mm:ss")
        : null
    );

    appendIfPresent(
      "OfferDisplayEndTime",
      parsedData?.OfferDisplayEndTime
        ? dayjs(parsedData?.OfferDisplayEndTime).format("HH:mm:ss")
        : null
    );
    appendIfPresent(
      "ButterflyEffectStartTime",
      parsedData?.ButterflyEffectStartTime
        ? dayjs(parsedData?.ButterflyEffectStartTime).format("HH:mm:ss")
        : null
    );
    appendIfPresent(
      "ButterflyEffectEndTime",
      parsedData?.ButterflyEffectEndTime
        ? dayjs(parsedData?.ButterflyEffectEndTime).format("HH:mm:ss")
        : null
    );
    appendIfPresent(
      "IsActive",
      alignment === OFFER_STATUS.ACTIVE ? "true" : "false"
    );
    appendIfPresent(
      "IsArchive",
      alignment === OFFER_STATUS.ARCHIVE ? "true" : "false"
    );
    appendIfPresent("DealerDate", date ? dayjs(time).toISOString() : null);
    appendIfPresent("DealerTime", time ? dayjs(time).format("HH:mm:ss") : null);
    appendIfPresent("WhatsAppNumber", phone);
    appendIfPresent("CountryDialCode", countryCode);

    if (!updateId || isClone) {
      try {
        setLoading(true);
        const response = await createOffer(formData).unwrap();
        setLoading(false);
        if (response?.status === 200) {
          removeFromStorage(STORAGE_KEYS.offerData);
          // navigate("/manage-offer/preview-offer-detail");
          showToast(response?.message || "");
          navigate("/manage-offer", {
            state: { status, categoryTab, categoryId, categoryName },
          });
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    } else {
      try {
        setLoading(true);
        const response = await updateOffer(formData).unwrap();
        setLoading(false);
        if (response?.status === 200) {
          removeFromStorage(STORAGE_KEYS.offerData);
          // navigate("/manage-offer/preview-offer-detail");
          showToast(response?.message || "");
          navigate("/manage-offer", {
            state: { status, categoryTab, categoryId, categoryName },
          });
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    }
  };

  const cloneOfferApi = async () => {
    try {
      const response = await cloneOffer({ offerId: updateId }).unwrap();
      if (response.status === 200) {
        removeFromStorage(STORAGE_KEYS.offerData);
        // navigate("/manage-offer/preview-offer-detail");
        showToast(response?.message || "");
        navigate("/manage-offer", {
          state: { status: 3, categoryTab, categoryId, categoryName },
        });
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    setCountryCode(
      parsedData?.CountryDialCode ?? offerDetails?.countryDialCode ?? "+971"
    );
    setPhone(parsedData?.WhatsAppNumber ?? offerDetails?.whatsAppNumber ?? "");

    setDate(
      parsedData?.DealerDate
        ? dayjs(parsedData?.DealerDate)
        : offerDetails?.dealerDate
          ? dayjs(offerDetails?.dealerDate)
          : null
    );
    setTime(
      parsedData?.DealerTime
        ? dayjs(parsedData?.DealerTime)
        : offerDetails?.dealerTime
          ? dayjs(offerDetails?.dealerDate)
          : null
    );
  }, []);

  return (
    <>
      <div className="section_title mn_hdng">
        <h2>Contact Dealer</h2>
        <div className="btn_flex flx_end">
          <Button className="btn btn_primary" onClick={() => setTabs(0)}>
            Back
          </Button>

          {alignment === "archived" && (
            <Button
              className="btn btn_primary"
              onClick={() => {
                if (updateId) {
                  cloneOfferApi();
                } else {
                  handleArchive();
                }
              }}
            >
              Save & Exit
            </Button>
          )}
          <Button className="btn btn_primary" onClick={handleSubmit}>
            Preview
          </Button>
        </div>
      </div>

      <div className="gap_m avnc_bkng">
        <div className="w_50 control_group date_field">
          <label> Date</label>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              className="form_control"
              format="DD/MM/YYYY"
              minDate={dayjs()}
              value={date}
              slotProps={{
                textField: {
                  placeholder: "Date",
                  sx: {
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#9999", // change text & placeholder color
                      opacity: 1,
                    },
                  },
                },
              }}
              onChange={(newValue) => setDate(newValue)}
              slots={{
                openPickerIcon: () => (
                  <img src="/static/images/calendar_icon.svg" alt="icon" />
                ),
                // textField: (params) => (
                //   <TextField
                //     {...params}
                //     fullWidth
                //     placeholder="Start Date"
                //     hiddenLabel
                //   />
                // ),
              }}
              desktopModeMediaQuery="(min-width:0px)"
            />
          </LocalizationProvider>
          {error && !date ? (
            <p className="err_msg">Start Date is required</p>
          ) : !updateId &&
            error &&
            date &&
            dayjs(date).isBefore(dayjs(), "day") ? (
            <p className="err_msg">Start Date cannot be earlier than today</p>
          ) : (
            ""
          )}
        </div>
        <div className="w_50 control_group date_field">
          <label> Time</label>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              value={time}
              onChange={(newValue) => {
                if (date) {
                  setTime(
                    dayjs(date)
                      .hour(dayjs(newValue).hour())
                      .minute(dayjs(newValue).minute())
                  );
                }
              }}
              slotProps={{
                textField: {
                  placeholder: "Time",
                  sx: {
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#9999", // change text & placeholder color
                      opacity: 1,
                    },
                  },
                },
              }}
              slots={{
                openPickerIcon: () => (
                  <img src="/static/images/calendar_icon.svg" alt="icon" />
                ),
              }}
              disabled={!date}
            />
          </LocalizationProvider>
          {error && !time ? <p className="err_msg">Time is required</p> : ""}
        </div>
        <div className="control_group w_50">
          <label>Phone Number*</label>
          <PhoneInput
            enableSearch
            value={countryCode + phone}
            country={"uae"}
            inputClass="phoneInput"
            onChange={(phone, country) => handleChangePhone(phone, country)}
            inputStyle={{ width: "100%", backgroundColor: "#232323" }}
            buttonClass="phoneBtn"
            placeholder="Phone Number"
          />
          <p className="err_msg">
            {error && !phone ? "Phone number is required" : ""}
          </p>
        </div>
      </div>
    </>
  );
}

export default ExperienceForm;
